import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const EMPTY_FORM = { visitorName: '', visitorCompany: '', purpose: '', hostName: '', visitedAt: '', enteredAt: '', policyId: '', notes: '' };

function today() {
  return new Date().toISOString().split('T')[0];
}

function nowLocal() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function SqfVisitorLogPage() {
  const { lang } = useLanguage();
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Filters
  const [dateFilter, setDateFilter]     = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // New / edit visitor modal
  const [showModal, setShowModal]       = useState(false);
  const [editingLog, setEditingLog]     = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);

  // Signing modal
  const [signingLog, setSigningLog]         = useState(null);
  const [signingPolicy, setSigningPolicy]   = useState(null);
  const [signatureName, setSignatureName]   = useState('');
  const [signingStep, setSigningStep]       = useState('read');
  const [savingSignature, setSavingSignature] = useState(false);

  // View signed record
  const [viewLog, setViewLog] = useState(null);

  // Exit time modal
  const [exitLog, setExitLog]   = useState(null);
  const [exitedAt, setExitedAt] = useState('');
  const [savingExit, setSavingExit] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (dateFilter) params.date = dateFilter;
    if (searchFilter) params.search = searchFilter;
    Promise.all([
      sqfService.getVisitorLogs(params),
      sqfService.getPolicies(),
    ])
      .then(([data, pol]) => {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setPolicies((pol || []).filter(p => !p.archivedAt && p.activeVersionId && p.category === 'visitor'));
      })
      .catch(() => setError(t('Failed to load visitor log.', 'Error al cargar el registro de visitantes.', lang)))
      .finally(() => setLoading(false));
  }, [dateFilter, searchFilter, lang]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const openNew = () => {
    setEditingLog(null);
    setForm({ ...EMPTY_FORM, visitedAt: today(), enteredAt: nowLocal() });
    setShowModal(true);
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setForm({
      visitorName:    log.visitorName || '',
      visitorCompany: log.visitorCompany || '',
      purpose:        log.purpose || '',
      hostName:       log.hostName || '',
      visitedAt:      log.visitedAt || today(),
      enteredAt:      log.enteredAt ? new Date(new Date(log.enteredAt).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      policyId:       log.policyId ? String(log.policyId) : '',
      notes:          log.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.visitorName.trim()) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        policyId: form.policyId ? Number(form.policyId) : null,
        // datetime-local gives a naive string with no timezone; convert to UTC ISO so the
        // server (which runs in UTC) stores the correct moment in time
        enteredAt: form.enteredAt ? new Date(form.enteredAt).toISOString() : null,
      };
      if (editingLog) {
        await sqfService.updateVisitorLog(editingLog.id, data);
      } else {
        await sqfService.createVisitorLog(data);
      }
      setShowModal(false);
      load();
    } catch {
      setError(t('Failed to save visitor.', 'Error al guardar visitante.', lang));
    } finally {
      setSaving(false);
    }
  };

  const openSigning = async (log) => {
    if (!log.policyId) {
      setError(t('No policy linked to this entry. Edit the entry and select a visitor policy first.', 'No hay política vinculada. Edite el registro y seleccione una política de visitantes primero.', lang));
      return;
    }
    try {
      const fullPolicy = await sqfService.getPolicy(log.policyId);
      const activeVersion = (fullPolicy.Versions || []).find(v => v.id === fullPolicy.activeVersionId);
      if (!activeVersion) {
        setError(t('The linked policy has no published version yet.', 'La política vinculada no tiene versión publicada.', lang));
        return;
      }
      setSigningLog(log);
      setSigningPolicy({ ...fullPolicy, activeVersion });
      setSignatureName('');
      setSigningStep('read');
      setSavingSignature(false);
    } catch {
      setError(t('Failed to load policy for signing.', 'Error al cargar la política para firmar.', lang));
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim() || !signingPolicy?.activeVersion) return;
    setSavingSignature(true);
    try {
      await sqfService.signVisitorLog(signingLog.id, {
        signatureName,
        policyVersionId: signingPolicy.activeVersion.id,
      });
      setSigningLog(null);
      setSigningPolicy(null);
      load();
    } catch {
      setError(t('Failed to save signature.', 'Error al guardar la firma.', lang));
    } finally {
      setSavingSignature(false);
    }
  };

  const openExit = (log) => {
    setExitLog(log);
    setExitedAt(nowLocal());
  };

  const handleSaveExit = async () => {
    if (!exitLog) return;
    setSavingExit(true);
    try {
      await sqfService.updateVisitorLog(exitLog.id, {
        exitedAt: exitedAt ? new Date(exitedAt).toISOString() : null,
      });
      setExitLog(null);
      load();
    } catch {
      setError(t('Failed to log exit.', 'Error al registrar la salida.', lang));
    } finally {
      setSavingExit(false);
    }
  };

  const publishedVisitorPolicies = policies;

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf/contractors" className="text-muted" style={{ fontSize: 12 }}>← {t('Contractors', 'Contratistas', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Visitor Log', 'Registro de Visitantes', lang)}</h4>
        </div>
        <div className="d-flex gap-2">
          <Link to="/sqf/policies" className="btn btn-outline-secondary btn-sm">{t('Policies', 'Políticas', lang)}</Link>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ {t('Log Visitor', 'Registrar Visitante', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}

      {/* Filters */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ maxWidth: 160 }}
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          placeholder="Filter by date"
        />
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 220 }}
          placeholder={t('Search name, company, purpose…', 'Buscar nombre, empresa, motivo…', lang)}
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
        />
        {(dateFilter || searchFilter) && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setDateFilter(''); setSearchFilter(''); }}>
            {t('Clear', 'Limpiar', lang)}
          </button>
        )}
        <span className="text-muted align-self-center ms-auto" style={{ fontSize: 12 }}>
          {total} {t('entries', 'entradas', lang)}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: 40 }}>📋</div>
          <p className="mt-2">{t('No visitor entries yet.', 'Aún no hay entradas de visitantes.', lang)}</p>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ {t('Log Visitor', 'Registrar Visitante', lang)}</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>{t('Date', 'Fecha', lang)}</th>
                <th>{t('Entry', 'Entrada', lang)}</th>
                <th>{t('Visitor', 'Visitante', lang)}</th>
                <th>{t('Company', 'Empresa', lang)}</th>
                <th>{t('Purpose', 'Motivo', lang)}</th>
                <th>{t('Host', 'Anfitrión', lang)}</th>
                <th>{t('Policy', 'Política', lang)}</th>
                <th>{t('Signature', 'Firma', lang)}</th>
                <th>{t('Exit', 'Salida', lang)}</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const isLocked = !!(log.signatureName && log.exitedAt);
                return (
                <tr key={log.id} className={isLocked ? 'table-light' : ''}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {log.visitedAt ? new Date(log.visitedAt + 'T12:00:00').toLocaleDateString() : '—'}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {log.enteredAt ? new Date(log.enteredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <span className="fw-semibold">{log.visitorName}</span>
                    {isLocked && <span className="ms-1 text-muted" title={t('Record locked — signed and exited', 'Registro bloqueado — firmado y con salida registrada', lang)}>🔒</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{log.visitorCompany || '—'}</td>
                  <td style={{ fontSize: 12 }}>{log.purpose || '—'}</td>
                  <td style={{ fontSize: 12 }}>{log.hostName || '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {log.Policy
                      ? <Link to={`/sqf/policies/${log.Policy.id}/edit`} className="text-decoration-none">{log.Policy.name}</Link>
                      : <span className="text-muted fst-italic">{t('None', 'Ninguna', lang)}</span>
                    }
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {log.signatureName
                      ? <span className="badge bg-success">{t('Signed', 'Firmado', lang)}</span>
                      : <span className="badge bg-secondary">{t('Unsigned', 'Sin firmar', lang)}</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {log.exitedAt
                      ? new Date(log.exitedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : <span className="text-muted">—</span>
                    }
                  </td>
                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                    {!isLocked && (
                      <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => openEdit(log)}>{t('Edit', 'Editar', lang)}</button>
                    )}
                    {log.signatureName ? (
                      <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => setViewLog(log)}>{t('View', 'Ver', lang)}</button>
                    ) : (
                      <button className="btn btn-outline-primary btn-sm me-1" onClick={() => openSigning(log)} disabled={!log.policyId}>
                        {t('Sign', 'Firmar', lang)}
                      </button>
                    )}
                    {!log.exitedAt && (
                      <button className="btn btn-outline-warning btn-sm" onClick={() => openExit(log)}>{t('Exit', 'Salida', lang)}</button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── New / Edit modal ── */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingLog ? t('Edit Visitor Entry', 'Editar Entrada de Visitante', lang) : t('Log Visitor', 'Registrar Visitante', lang)}
                </h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Visitor Name *', 'Nombre del Visitante *', lang)}</label>
                  <input className="form-control form-control-sm" value={form.visitorName} onChange={e => setField('visitorName', e.target.value)} autoFocus />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Company / Organization', 'Empresa / Organización', lang)}</label>
                  <input className="form-control form-control-sm" value={form.visitorCompany} onChange={e => setField('visitorCompany', e.target.value)} />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Purpose of Visit', 'Motivo de la Visita', lang)}</label>
                  <input className="form-control form-control-sm" value={form.purpose} onChange={e => setField('purpose', e.target.value)} placeholder={t('e.g. Audit, Delivery, Meeting…', 'Ej. Auditoría, Entrega, Reunión…', lang)} />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Host / Contact', 'Anfitrión / Contacto', lang)}</label>
                  <input className="form-control form-control-sm" value={form.hostName} onChange={e => setField('hostName', e.target.value)} placeholder={t('Who are they visiting?', '¿A quién visitan?', lang)} />
                </div>
                <div className="row g-2 mb-2">
                  <div className="col">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Date', 'Fecha', lang)}</label>
                    <input type="date" className="form-control form-control-sm" value={form.visitedAt} onChange={e => setField('visitedAt', e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Entry time', 'Hora de entrada', lang)}</label>
                    <input type="datetime-local" className="form-control form-control-sm" value={form.enteredAt} onChange={e => setField('enteredAt', e.target.value)} />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Visitor Policy', 'Política de Visitantes', lang)}</label>
                  <select className="form-select form-select-sm" value={form.policyId} onChange={e => setField('policyId', e.target.value)}>
                    <option value="">{t('None', 'Ninguna', lang)}</option>
                    {publishedVisitorPolicies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {publishedVisitorPolicies.length === 0 && (
                    <div className="form-text">
                      {t('No published visitor policies.', 'No hay políticas de visitantes publicadas.')}{' '}
                      <Link to="/sqf/policies">{t('Create one →', 'Crear una →', lang)}</Link>
                    </div>
                  )}
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: 13 }}>{t('Notes', 'Notas', lang)}</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.visitorName.trim()}>
                  {saving ? t('Saving…', 'Guardando…', lang) : editingLog ? t('Save Changes', 'Guardar Cambios', lang) : t('Log Visitor', 'Registrar Visitante', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Signing modal ── */}
      {signingLog && signingPolicy && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{signingPolicy.name}</h5>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {signingLog.visitorName}{signingLog.visitorCompany ? ` · ${signingLog.visitorCompany}` : ''}
                    {' · '}v{signingPolicy.activeVersion.versionNumber}
                    {signingPolicy.activeVersion.revisionDate ? ` (${signingPolicy.activeVersion.revisionDate})` : ''}
                  </div>
                </div>
                <button className="btn-close" onClick={() => setSigningLog(null)} />
              </div>

              {signingStep === 'read' ? (
                <>
                  <div className="modal-body">
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: 460, overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: signingPolicy.activeVersion.content || `<p class="text-muted">${t('No content.', 'Sin contenido.', lang)}</p>` }}
                    />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setSigningLog(null)}>{t('Cancel', 'Cancelar', lang)}</button>
                    <button className="btn btn-primary" onClick={() => setSigningStep('sign')}>
                      {t('I Have Read the Policy →', 'He leído la política →', lang)}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-body">
                    <button className="btn btn-link btn-sm p-0 mb-3 text-muted" style={{ fontSize: 12 }} onClick={() => setSigningStep('read')}>
                      ← {t('Re-read policy', 'Releer la política', lang)}
                    </button>
                    <div className="mb-1">
                      <label className="form-label fw-semibold">{t('Electronic Signature — Type your full legal name', 'Firma electrónica — Escriba su nombre legal completo', lang)}</label>
                      <input
                        className="form-control"
                        style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 18 }}
                        placeholder={t('Full name…', 'Nombre completo…', lang)}
                        value={signatureName}
                        onChange={e => setSignatureName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                      {t(
                        'By typing your name above, you acknowledge that you have read and agree to the visitor policy.',
                        'Al escribir su nombre, reconoce que ha leído y acepta la política de visitantes.',
                        lang
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setSigningLog(null)}>{t('Cancel', 'Cancelar', lang)}</button>
                    <button className="btn btn-success" onClick={handleSign} disabled={savingSignature || !signatureName.trim()}>
                      {savingSignature ? t('Saving…', 'Guardando…', lang) : t('✓ Sign & Save', '✓ Firmar y Guardar', lang)}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── View signed record modal ── */}
      {viewLog && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{t('Signed Visitor Policy', 'Política de Visitante Firmada', lang)}</h5>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {viewLog.visitorName}{viewLog.visitorCompany ? ` · ${viewLog.visitorCompany}` : ''}
                  </div>
                </div>
                <button className="btn-close" onClick={() => setViewLog(null)} />
              </div>
              <div className="modal-body">
                {viewLog.SignedVersion?.content ? (
                  <div
                    className="border rounded p-3 mb-4"
                    style={{ maxHeight: 320, overflowY: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: viewLog.SignedVersion.content }}
                  />
                ) : (
                  <div className="text-muted mb-4" style={{ fontSize: 13 }}>{t('Policy text not available for this record.', 'Texto de política no disponible para este registro.', lang)}</div>
                )}
                <div className="border rounded p-3 bg-light">
                  <div className="text-muted mb-1" style={{ fontSize: 12 }}>{t('Electronic Signature', 'Firma Electrónica', lang)}</div>
                  <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 22, borderBottom: '1px solid #aaa', paddingBottom: 4, marginBottom: 8 }}>
                    {viewLog.signatureName}
                  </div>
                  <div className="d-flex gap-4 flex-wrap" style={{ fontSize: 12, color: '#555' }}>
                    <span><strong>{t('Policy:', 'Política:', lang)}</strong> {viewLog.Policy?.name || '—'}</span>
                    <span><strong>{t('Version:', 'Versión:', lang)}</strong> {viewLog.SignedVersion ? `v${viewLog.SignedVersion.versionNumber}` : '—'}</span>
                    <span><strong>{t('Signed:', 'Firmado:', lang)}</strong> {viewLog.signedAt ? new Date(viewLog.signedAt).toLocaleString() : '—'}</span>
                    <span><strong>{t('Visit date:', 'Fecha de visita:', lang)}</strong> {viewLog.visitedAt ? new Date(viewLog.visitedAt + 'T12:00:00').toLocaleDateString() : '—'}</span>
                    {viewLog.signatureIp && <span><strong>IP:</strong> {viewLog.signatureIp}</span>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setViewLog(null)}>{t('Close', 'Cerrar', lang)}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Exit time modal ── */}
      {exitLog && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 360 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Log Exit Time', 'Registrar Hora de Salida', lang)}</h5>
                <button className="btn-close" onClick={() => setExitLog(null)} />
              </div>
              <div className="modal-body">
                <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                  {exitLog.visitorName}{exitLog.visitorCompany ? ` · ${exitLog.visitorCompany}` : ''}
                </div>
                <label className="form-label fw-semibold">{t('Exit date & time', 'Fecha y hora de salida', lang)}</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={exitedAt}
                  onChange={e => setExitedAt(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setExitLog(null)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-warning" onClick={handleSaveExit} disabled={savingExit}>
                  {savingExit ? t('Saving…', 'Guardando…', lang) : t('Save Exit', 'Guardar Salida', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
